/**
 * AuthContext (feature-owned).
 *
 * Rewritten from the 418-LOC `contexts/AuthContext` to:
 *   - consume `useCurrentProfile` (RTK Query) instead of fetching profile +
 *     roles inline — the single-cache-key dedups the duplicate boot calls
 *     that MFAGuard + MFALoginFlow also produced
 *   - be TypeScript with the exact `AuthContextValue` shape the legacy
 *     `AuthContext.d.ts` declared, so the 150+ existing `useAuth()` call
 *     sites compile without changes
 *   - use the unified `useSignOut` hook so the RTK cache reset is guaranteed
 *
 * Session lifecycle (subscribe to `onAuthStateChange`, persistent storage,
 * Sentry tagging, local-storage profile cache for offline) stays here —
 * those are cross-cutting browser-plane concerns that don't belong in a
 * data hook.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Sentry from "@sentry/react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../../../lib/supabase";
import { useCurrentProfile } from "../hooks/useCurrentProfile";
import { useSignOut as useFeatureSignOut } from "../hooks/useSignOut";
import { useTrustedDeviceRevocationListener } from "../hooks/useTrustedDeviceRevocationListener";
import type {
  AuthContextValue,
  AuthProviderProps,
  UserProfile,
} from "../types/authTypes";

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Offline-survivable profile cache (kept from the legacy implementation so
// short offline periods don't blank the UI).
// ---------------------------------------------------------------------------

const LS_PROFILE_KEY = "fw_last_user_profile";

function loadCachedProfile(): UserProfile | null {
  try {
    const raw = localStorage.getItem(LS_PROFILE_KEY);
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

function saveCachedProfile(profile: UserProfile): void {
  try {
    localStorage.setItem(LS_PROFILE_KEY, JSON.stringify(profile));
  } catch {
    // Quota exceeded / disabled storage — no-op.
  }
}

function clearCachedProfile(): void {
  try {
    localStorage.removeItem(LS_PROFILE_KEY);
  } catch {
    // No-op.
  }
}

function isLikelyNetworkError(msg: string | null | undefined): boolean {
  const m = String(msg || "").toLowerCase();
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return (
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("network error") ||
    m.includes("timeout") ||
    m.includes("fetch failed")
  );
}

/** Dedupes noisy duplicate SIGNED_IN/INITIAL_SESSION emissions (tab focus, multi-tab sync). */
function sessionFingerprint(session: Session | null): string | null {
  if (!session?.access_token) return null;
  return `${session.access_token}:${String(session.expires_at ?? "")}:${session.user?.id ?? ""}`;
}

/**
 * Last-known session read straight from localStorage — the boot fallback
 * for when `supabase.auth.getSession()` stalls. supabase-js serialises
 * auth-token reads behind a `navigator.locks` lock; a crashed or
 * OS-suspended tab can hold `lock:sb-<ref>-auth-token` indefinitely, and
 * then `getSession()` never resolves. Returns null if nothing usable is
 * stored (older builds wrap the session under `currentSession`).
 */
function readPersistedSession(): Session | null {
  try {
    const key = (supabase.auth as unknown as { storageKey?: string })
      .storageKey;
    if (!key || typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as
      | (Partial<Session> & { currentSession?: Partial<Session> })
      | null;
    const sess = parsed?.access_token ? parsed : parsed?.currentSession;
    return sess?.access_token && sess?.user ? (sess as Session) : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  // Hydrate from localStorage on mount so a cold boot with a stale (but
  // valid) session can render the UI before the profile query resolves —
  // or survive a transient network blip that makes the query fail.
  // Updated whenever the RTK query returns a fresh profile (see below).
  const [cachedProfile, setCachedProfile] = useState<UserProfile | null>(() =>
    loadCachedProfile(),
  );
  const storagePersistRequestedRef = useRef(false);
  const prevUserIdRef = useRef<string | null>(null);
  const lastSessionFingerprintRef = useRef<string | null>(null);

  // Extract profile ID from user metadata, fall back to the auth user id.
  const rawMetadata = user?.user_metadata as
    | { profile_id?: string }
    | undefined;
  const profileId: string | null =
    rawMetadata?.profile_id ?? user?.id ?? null;

  // Compute `needsMfa` from the LIVE access token, not from any cached
  // query data. This is the only correct source of truth — the JWT
  // claims update on every token refresh, while RTK Query caches by
  // profileId and would happily serve stale "needsMfa=true" forever
  // even after the user reaches AAL2.
  //
  // Decoded claims of interest:
  //   - `aal`                                 (aal1 | aal2)
  //   - `app_metadata.has_verified_mfa_factor` (stamped by
  //     `public.custom_access_token_hook` at JWT issuance)
  //
  // Rule: caller `needsMfa` when they have factors enrolled but
  // haven't proved them this session.
  const needsMfa = useMemo(() => {
    const token = session?.access_token;
    if (!token) return false;
    try {
      const payload = JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
      ) as {
        aal?: string;
        app_metadata?: { has_verified_mfa_factor?: boolean };
      };
      const hasFactor = !!payload.app_metadata?.has_verified_mfa_factor;
      const isAal1 = payload.aal !== "aal2";
      return hasFactor && isAal1;
    } catch {
      return false;
    }
  }, [session?.access_token]);

  // Single source of truth for profile + roles — RTK Query, keyed on
  // profileId, so MFAGuard/MFALoginFlow hit the same cache slot.
  // Skip the query entirely when the caller needs MFA — the
  // `vw_react_user_profiles_min` read would RLS-reject with 42501 anyway,
  // and we don't want that error to populate the cache or surface as a
  // hard "missing-role" page. MFAGuard renders the challenge; after AAL2,
  // `needsMfa` flips false and the query runs.
  const profileQuery = useCurrentProfile({
    profileId: needsMfa ? null : profileId,
  });
  const { signOut: runSignOut } = useFeatureSignOut();

  const schoolId = profileQuery.profile?.school_id ?? null;

  // Surface only fatal profile errors (not transient network blips).
  useEffect(() => {
    if (!profileQuery.error) {
      setAuthError(null);
      return;
    }
    if (isLikelyNetworkError(profileQuery.error)) {
      // Keep last known profile; log breadcrumb only.
      try {
        Sentry.addBreadcrumb({
          category: "auth",
          level: "warning",
          message: "Profile load failed due to network; relying on cache",
        });
      } catch {
        // Sentry init may be missing in tests — ignore.
      }
      setAuthError(null);
    } else {
      setAuthError(
        `Authentication failed: ${profileQuery.error}. Please contact IT support.`,
      );
      clearCachedProfile();
    }
  }, [profileQuery.error]);

  // Tag Sentry + persist cache on successful profile loads.
  useEffect(() => {
    const profile = profileQuery.profile;
    if (!profile) return;
    saveCachedProfile(profile);
    setCachedProfile(profile);
    try {
      Sentry.setUser({ id: profile.id });
      Sentry.setTags({
        role: profile.role || "unknown",
        school_id: profile.school_id || "unknown",
        aal: profileQuery.aal ?? "unknown",
      });
    } catch {
      // Sentry may not be configured in some environments.
    }
  }, [profileQuery.profile, profileQuery.aal]);

  // One-shot: request persistent storage so the session survives aggressive
  // browser storage eviction on mobile.
  const ensurePersistentStorage = useCallback(async () => {
    try {
      if (!("storage" in navigator) || !navigator.storage?.persist) return;
      if (storagePersistRequestedRef.current) return;
      storagePersistRequestedRef.current = true;
      const already = await navigator.storage.persisted();
      if (already) return;
      await navigator.storage.persist();
    } catch {
      // No-op — best-effort.
    }
  }, []);

  // Initial session check + onAuthStateChange subscription.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Race getSession() against a watchdog. supabase-js serialises
        // auth-token reads behind a navigator-locks lock; a crashed or
        // OS-suspended tab can hold that lock forever, and without this
        // timeout the app hangs on the boot "Loading…" screen
        // indefinitely. On timeout we boot from the persisted session.
        const raced = await Promise.race([
          supabase.auth.getSession(),
          new Promise<"timeout">((resolve) =>
            setTimeout(() => resolve("timeout"), 5000),
          ),
        ]);
        if (cancelled) return;
        let initial: Session | null;
        if (raced === "timeout") {
          initial = readPersistedSession();
          console.warn(
            "[AuthContext] getSession() timed out — booting from persisted session",
          );
        } else if (raced.error) {
          console.error(
            "[AuthContext] initial session check failed",
            raced.error,
          );
          setSessionChecked(true);
          return;
        } else {
          initial = raced.data.session;
        }
        if (initial?.user) {
          prevUserIdRef.current = initial.user.id;
          lastSessionFingerprintRef.current = sessionFingerprint(initial);
          setSession(initial);
          setUser(initial.user);
          void ensurePersistentStorage();
          // Propagate the authenticated JWT to the Realtime client BEFORE
          // any feature hook calls supabase.channel(...). Without this,
          // every postgres_changes subscription registers with
          // claims_role = 'anon' (the apikey role), and the
          // realtime.subscription_check_filters trigger rejects any filter
          // on a column anon lacks SELECT on (e.g. school_id) with
          // "invalid column for filter <col>". Setting the JWT here covers
          // the cold-start path; the onAuthStateChange handler below
          // covers SIGNED_IN / TOKEN_REFRESHED / SIGNED_OUT transitions.
          supabase.realtime.setAuth(initial.access_token);
          // Cached profile was already hydrated via useState initializer;
          // nothing more to do here.
        }
      } catch (err) {
        console.error("[AuthContext] session check exception", err);
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Defer to next tick — Supabase recommends this to avoid forced
      // redraws on tab focus (issue #7250).
      setTimeout(() => {
        if (
          (event === "SIGNED_IN" ||
            event === "TOKEN_REFRESHED" ||
            event === "INITIAL_SESSION") &&
          newSession
        ) {
          // Always refresh Realtime's JWT — even when the session
          // fingerprint is unchanged, TOKEN_REFRESHED carries a new
          // access_token, and Realtime keeps using whichever token it
          // last had until told otherwise. Without this, channels
          // subscribe with claims_role='anon' and the
          // subscription_check_filters trigger rejects most filters.
          supabase.realtime.setAuth(newSession.access_token);
          const fp = sessionFingerprint(newSession);
          const isDuplicateSession =
            fp != null && fp === lastSessionFingerprintRef.current;
          if (isDuplicateSession) {
            return;
          }
          if (fp != null) {
            lastSessionFingerprintRef.current = fp;
          }
          const incomingUserId = newSession.user?.id ?? null;
          const userChanged = prevUserIdRef.current !== incomingUserId;
          prevUserIdRef.current = incomingUserId;
          setSession(newSession);
          if (userChanged) {
            setUser(newSession.user);
            void ensurePersistentStorage();
          }
        } else if (event === "SIGNED_OUT") {
          prevUserIdRef.current = null;
          lastSessionFingerprintRef.current = null;
          setSession(null);
          setUser(null);
          setAuthError(null);
          clearCachedProfile();
          setCachedProfile(null);
          // Drop the user JWT from Realtime by reverting to the anon
          // apikey. realtime-js's setAuth treats falsy input as "use the
          // accessToken callback fallback" rather than clearing — so we
          // pass the anon key explicitly. `supabaseKey` is set publicly on
          // the client by createClient (SupabaseClient.js).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const anonKey = (supabase as any).supabaseKey as string;
          if (anonKey) {
            void supabase.realtime.setAuth(anonKey);
          }
          // Clear MFA-passed latch so the next sign-in (even within the
          // same browser tab) starts fresh. Latch lives in sessionStorage
          // and MFAGuard reads it lazily on mount.
          try {
            sessionStorage.removeItem("flexwise.mfa.passed");
          } catch {
            // Best-effort only.
          }
          try {
            Sentry.setUser(null);
          } catch {
            // No-op.
          }
        }
      }, 0);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [ensurePersistentStorage]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      if (!data.user || !data.session) {
        throw new Error("signIn returned no user/session");
      }
      return { user: data.user, session: data.session };
    },
    [],
  );

  const signOut = useCallback(async () => {
    await runSignOut("global");
  }, [runSignOut]);

  // F-024 v2: when another browser revokes the trusted device that
  // minted *this* browser's AAL2 session, push a sign-out within ~1s
  // via Postgres-changes on `public.trusted_devices`. Pairs with
  // JWT_EXPIRY=300 as the worst-case fallback ceiling.
  useTrustedDeviceRevocationListener(session?.access_token ?? null);

  const refreshProfile = useCallback(async () => {
    // `refetch()` returns a thunk-result that resolves when the query
    // settles. Awaiting it means callers (e.g. App.jsx's onMFAComplete)
    // know the profile data is current before they navigate — without
    // this await, the call resolves immediately and any role-based
    // routing decision races the in-flight fetch.
    try {
      await profileQuery.refetch().unwrap();
    } catch {
      // Best-effort refresh — keep going even if the refetch errors so
      // the caller can fall back to whatever cached profile is present.
    }
  }, [profileQuery]);

  // When the access-token's AAL changes (e.g. user just completed MFA and
  // got an AAL2 token), the cached profile query may still hold an old
  // AAL1 result (or a stale "needsMfa=true" payload from a pre-fix
  // build). Force a refetch on AAL transitions so the new AAL2 read of
  // `user_profiles` actually fires.
  const prevAalRef = useRef<string | null>(null);
  useEffect(() => {
    const token = session?.access_token;
    if (!token) {
      prevAalRef.current = null;
      return;
    }
    let aal: string | null = null;
    try {
      const payload = JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
      ) as { aal?: string };
      aal = payload.aal ?? null;
    } catch {
      aal = null;
    }
    if (prevAalRef.current !== null && prevAalRef.current !== aal) {
      profileQuery.refetch();
    }
    prevAalRef.current = aal;
    // We deliberately exclude `profileQuery` from deps — including it
    // would re-run this effect on every query state change (loading
    // flips etc.) and cause a refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]);

  // Live profile from RTK, fallback to cached value when the query is
  // pending / errored on a transient network blip. Keeps the UI filled
  // from the previous session while the fresh fetch is in-flight, which
  // is what the "offline-survivable cache" was always supposed to do.
  const userProfile = profileQuery.profile ?? cachedProfile;

  // `loading` = we haven't checked the session yet, OR we have a user
  // but no profile AT ALL (neither live nor cached). If we have a cached
  // profile we render immediately rather than flashing a spinner.
  const loading =
    !sessionChecked || (!!user && !userProfile && profileQuery.loading);

  // Effective school/profile id: fall back to cached profile when the
  // live query hasn't returned yet, so hooks that key on schoolId don't
  // see a null on cold boot just because the network is slow.
  const effectiveSchoolId = schoolId ?? cachedProfile?.school_id ?? null;
  const effectiveProfileId = profileId ?? cachedProfile?.id ?? null;

  const value: AuthContextValue = {
    user,
    session,
    loading,
    userProfile,
    needsMfa,
    authError,
    profileId: effectiveProfileId,
    schoolId: effectiveSchoolId,
    signIn,
    signOut,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
